import React, { useState, useEffect } from 'react';
import { fetchOrders } from '../../services/shipStationAPI';
import { tagDictionaryMaker } from '../../contexts/TagStorage';
import { fetchTags } from '../../services/shipStationAPI';
import type { ShipstationOrder } from '../../types/apiTypes';
import { createMemoryTtlCache } from '../../utils/memoryTtlCache';
import { useOrdersListHideIssues } from '../../hooks/useOrdersListHideIssues';
import OrderBox from './OrderBox';
import OrderSearchModal from './OrderSearchModal';
import OrdersListNavbarActions from './OrdersListNavbarActions';
import { filterOrdersHidingIssues } from './orderSearchUtils';
import { buildOrderDetailNavState } from './orderdetail/orderDetailNavState';
import './OrdersList.css';

interface OrdersListProps {
  readonly fromPath?: string;
}

type OrdersListCachePayload = {
  orders: ShipstationOrder[];
  tagDictionary: Record<string, string | number>;
};

const ordersListCache = createMemoryTtlCache<OrdersListCachePayload>(60_000);

function readCachedPayload(): OrdersListCachePayload | null {
  return ordersListCache.isValid() ? ordersListCache.get() : null;
}

function OrdersList({
  fromPath = '/orders-list',
}: OrdersListProps): React.ReactElement {
  const initial = readCachedPayload();
  const [orders, setOrders] = useState<ShipstationOrder[]>(initial?.orders ?? []);
  const [loading, setLoading] = useState(!initial);
  const [tagDictionary, setTagDictionary] = useState<Record<string, string | number>>(initial?.tagDictionary ?? {});
  const [showOrderSearch, setShowOrderSearch] = useState(false);
  const { hideIssues, toggleHideIssues } = useOrdersListHideIssues();

  useEffect(() => {
    let cancelled = false;

    const cached = readCachedPayload();
    if (cached) {
      setOrders(cached.orders);
      setTagDictionary(cached.tagDictionary);
      setLoading(false);
      return;
    }

    let nextOrders: ShipstationOrder[] = [];
    let nextTagDictionary: Record<string, string | number> = {};

    const getOrders = async () => {
      const data = await fetchOrders();
      if (cancelled) return;
      nextOrders = data.orders;
      setOrders(data.orders);
    };

    const makeTagDictionary = async () => {
      const data = await fetchTags();
      if (cancelled) return;
      nextTagDictionary = tagDictionaryMaker(data);
      setTagDictionary(nextTagDictionary);
    };

    Promise.all([getOrders(), makeTagDictionary()]).then(() => {
      if (cancelled) return;
      ordersListCache.set({ orders: nextOrders, tagDictionary: nextTagDictionary });
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  const navbarActions = (
    <OrdersListNavbarActions
      hideIssues={hideIssues}
      onToggleHideIssues={toggleHideIssues}
      onOpenSearch={() => setShowOrderSearch(true)}
    />
  );

  if (loading || !tagDictionary) {
    return (
      <div className="orders-container">
        {navbarActions}
        <p className="orders-list-status">Loading...</p>
      </div>
    );
  }

  const visibleOrders = filterOrdersHidingIssues(orders, tagDictionary, hideIssues);
  const navState = buildOrderDetailNavState(fromPath, visibleOrders);

  return (
    <div className="orders-container">
      {navbarActions}
      {orders.length === 0 ? <p className="orders-list-status">No Orders</p> : null}
      <ul>
        {visibleOrders.map((order) => (
            <OrderBox
              key={order.orderId}
              order={order}
              tagDictionary={tagDictionary}
              navState={navState}
            />
        ))}
      </ul>
      <OrderSearchModal
        open={showOrderSearch}
        orders={visibleOrders}
        tagDictionary={tagDictionary}
        navState={navState}
        onClose={() => setShowOrderSearch(false)}
      />
    </div>
  );
}

export default OrdersList;
